import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useMesh } from '../../hooks/useSummaryMesh';
import SceneErrorBoundary from './SceneErrorBoundary';

/**
 * Рендер скайбокса (звёзды, туманности, планеты, луны, астероиды + distant_objects).
 *
 * Источники:
 *   - Звёзды/туманности — `stars` из `DynamicSkybox` (через сокет или REST).
 *   - Celestial bodies — `planets`, `moons`, `asteroids` (REST, один раз).
 *   - Крупные объекты в соседних эшелонах — `distant_objects`
 *     (супершипы, станции, верфи).
 *
 * @param {object} props
 * @param {object} props.skybox — данные скайбокса:
 *   {
 *     stars: [...],
 *     planets: [...],
 *     moons: [...],
 *     asteroids: [...],
 *     distant_objects: [...],
 *     metadata: { ... }
 *   }
 * @param {number} [props.range=500] — радиус сферы для distant_objects
 * @param {boolean} [props.showDistantObjects=true] — рендерить ли distant_objects
 * @param {React.ReactNode} [props.fallback=null] — что рендерить пока нет данных
 *
 * @example
 *   <SkyboxMesh skybox={skyboxData} range={500} />
 */
export default function SkyboxMesh({
  skybox,
  range = 500,
  showDistantObjects = true,
  fallback = null,
}) {
  if (!skybox) return fallback;

  const stars = skybox.stars || [];
  const planets = skybox.planets || [];
  const moons = skybox.moons || [];
  const asteroids = skybox.asteroids || [];
  const distantObjects = skybox.distant_objects || [];

  return (
    <SceneErrorBoundary fallback={fallback} label="SkyboxMesh">
      <group name="skybox">
        {/* Звёзды (точки) */}
        <StarField stars={stars} />

        {/* Планеты */}
        {planets.map((planet, idx) => (
          <CelestialBody
            key={planet.planet_id || `planet-${idx}`}
            body={planet}
            bodyType="planet"
          />
        ))}

        {/* Луны */}
        {moons.map((moon, idx) => (
          <CelestialBody
            key={moon.moon_id || `moon-${idx}`}
            body={moon}
            bodyType="moon"
          />
        ))}

        {/* Астероиды */}
        {asteroids.map((asteroid, idx) => (
          <CelestialBody
            key={asteroid.asteroid_id || `asteroid-${idx}`}
            body={asteroid}
            bodyType="asteroid"
          />
        ))}

        {/* Крупные объекты в соседних эшелонах */}
        {showDistantObjects && distantObjects.length > 0 && (
          <DistantObjects objects={distantObjects} range={range} />
        )}
      </group>
    </SceneErrorBoundary>
  );
}

// ============================================================================
// STAR FIELD
// ============================================================================

function StarField({ stars }) {
  if (!Array.isArray(stars) || stars.length === 0) return null;

  return (
    <group name="star-field">
      {stars.map((star, idx) => (
        <Star
          key={star.id || star.name || `star-${idx}`}
          star={star}
        />
      ))}
    </group>
  );
}

function Star({ star }) {
  const position = useMemo(() => {
    // Позиция на сфере
    const pos = star.position || {};
    const x = Number(pos.x || 0);
    const y = Number(pos.y || 0);
    const z = Number(pos.z || 0);
    const distance = star.distance || 1000;

    const len = Math.sqrt(x * x + y * y + z * z) || 1;
    return [(x / len) * distance, (y / len) * distance, (z / len) * distance];
  }, [star]);

  const color = useMemo(() => {
    if (star.color) {
      if (typeof star.color === 'string') return star.color;
      const { r = 1, g = 1, b = 1 } = star.color;
      return rgbToHex(r, g, b);
    }
    return '#ffffff';
  }, [star]);

  const size = Math.max(0.5, (star.intensity || 1) * 2);

  return (
    <mesh position={position}>
      <sphereGeometry args={[size, 6, 6]} />
      <meshBasicMaterial color={color} />
    </mesh>
  );
}

// ============================================================================
// CELESTIAL BODY (планета, луна, астероид)
// ============================================================================

function CelestialBody({ body, bodyType }) {
  const [worldPosition, setWorldPosition] = useState([0, 0, 0]);

  useEffect(() => {
    // TODO: позиция в мире (орбитальная механика)
    if (body.world_position) {
      const [x, y, z] = normalizeVec3(body.world_position);
      setWorldPosition([x, y, z]);
    }
  }, [body]);

  const baseSphere = body.base_sphere;
  const baseColor = body.base_color || '#808080';
  const atmosphere = body.atmosphere;

  if (!baseSphere) return null;

  // Масштаб: реальный радиус в км → отображение (делим на 1000)
  const displayRadius = (body.radius_km || 1000) / 1000;

  return (
    <group position={worldPosition} name={`${bodyType}-${body.planet_id || body.moon_id || body.asteroid_id}`}>
      {/* Базовая сфера */}
      <mesh>
        <sphereGeometry args={[displayRadius, 32, 16]} />
        <meshStandardMaterial
          color={baseColor}
          emissive={baseColor}
          emissiveIntensity={0.1}
          roughness={0.8}
          metalness={0.2}
        />
      </mesh>

      {/* Атмосфера (если есть) */}
      {atmosphere && (
        <mesh scale={[1.02, 1.02, 1.02]}>
          <sphereGeometry args={[displayRadius, 32, 16]} />
          <meshBasicMaterial
            color={atmosphere.color || '#87CEEB'}
            transparent
            opacity={atmosphere.opacity || 0.3}
            side={2}
          />
        </mesh>
      )}
    </group>
  );
}

// ============================================================================
// DISTANT OBJECTS (супершипы, станции, верфи)
// ============================================================================

function DistantObjects({ objects, range }) {
  return (
    <group name="distant-objects">
      {objects.map((obj) => (
        <DistantObject
          key={obj.object_id}
          obj={obj}
          range={range}
        />
      ))}
    </group>
  );
}

function DistantObject({ obj, range }) {
  const [dx, dy, dz] = normalizeVec3(obj.direction || [0, 0, 1]);
  const velocity = obj.velocity || [0, 0, 0];

  const size = Math.max(0.5, obj.apparent_size || 1);
  const color = obj.color || '#cccccc';

  // Начальная позиция на сфере (range от камеры)
  const [position, setPosition] = useState([dx * range, dy * range, dz * range]);

  // Прогноз позиции по velocity (плавное движение)
  const lastUpdateRef = useRef(performance.now());
  const basePosRef = useRef([dx * range, dy * range, dz * range]);

  useEffect(() => {
    let animFrameId = null;

    const tick = () => {
      const now = performance.now();
      const dt = (now - lastUpdateRef.current) / 1000; // секунды
      lastUpdateRef.current = now;

      const [vx, vy, vz] = velocity;
      const [bx, by, bz] = basePosRef.current;

      // Простая линейная интерполяция (в реальности — орбитальная механика)
      const nx = bx + vx * dt * 0.01;
      const ny = by + vy * dt * 0.01;
      const nz = bz + vz * dt * 0.01;

      setPosition([nx, ny, nz]);

      animFrameId = requestAnimationFrame(tick);
    };

    animFrameId = requestAnimationFrame(tick);

    return () => {
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
  }, [velocity]);

  return (
    <mesh position={position} name={`distant-${obj.object_id}`}>
      <sphereGeometry args={[size, 8, 8]} />
      <meshBasicMaterial color={color} />
    </mesh>
  );
}

// ============================================================================
// UTILS
// ============================================================================

function normalizeVec3(v) {
  if (Array.isArray(v) && v.length >= 3) {
    return [Number(v[0]) || 0, Number(v[1]) || 0, Number(v[2]) || 0];
  }
  if (v && typeof v === 'object') {
    return [Number(v.x) || 0, Number(v.y) || 0, Number(v.z) || 0];
  }
  return [0, 0, 0];
}

function rgbToHex(r, g, b) {
  const toHex = (n) => {
    const v = Math.max(0, Math.min(255, Math.round(Number(n) * 255)));
    return v.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}